import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { UserDisplay } from "@components/common/user-display";
import { RemoveMemberSuccessDescription } from "@components/remove-member-success-description";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { BaseProjectProps } from "@custom-types/project-types";
import { Alert, Dialog, Text, useToast } from "@flat-ui";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { Stack } from "@mui/material";
import {
  ProjectMember,
  SelectedCardItemOption,
} from "@pages/projects/project-member";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { fetchingProjectsFlagsSelector } from "@store/projects/projects-selector";
import { removeMemberFromProject } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  ProjectMemberEvents,
  TableType,
} from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";

interface Props extends BaseProjectProps {
  /** List of members to display */
  members: FaroCoreApiTypes.ProjectMemberBase[];

  /** Location from which the member list is rendered, for tracking purposes. */
  location: "share-project-dialog";
}

/** Renders the members of a project in the share dialog */
export function ProjectMemberList({
  project,
  members,
  location,
}: Props): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const { openToast } = useToast();
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const { handleErrorWithToast } = useErrorContext();
  const { isRemovingProjectMember } = useAppSelector(
    fetchingProjectsFlagsSelector
  );
  const { canDeleteMemberFromProject, canChangeRole } =
    useHasUserValidPermissionProjectLevel({
      project,
    });

  const [memberToRemove, setMemberToRemove] =
    useState<FaroCoreApiTypes.ProjectMemberBase | null>(null);
  const [isRemovingMember, setIsRemovingMember] = useState(false);

  async function removeMember(): Promise<void> {
    if (isRemovingMember || isRemovingProjectMember || !memberToRemove) {
      return;
    }

    setIsRemovingMember(true);

    trackEvent({
      name: ProjectMemberEvents.removeMember,
      props: { location, dataType: TableType.projectMembers },
    });

    try {
      await dispatch(
        removeMemberFromProject({
          companyId,
          projectId: project.id,
          member: memberToRemove,
        })
      ).unwrap();

      setMemberToRemove(null);

      openToast({
        title: "Member removed from project",
        message: (
          <RemoveMemberSuccessDescription
            member={memberToRemove}
            subjectType="project"
            entityName={project.name}
          />
        ),
      });
    } catch (error) {
      handleErrorWithToast({
        id: `removeMemberFromProject-${Date.now().toString()}`,
        title: "Could not remove member from project",
        error,
      });
    } finally {
      setIsRemovingMember(false);
    }
  }

  if (members.length === 0) {
    return <Alert variant="info" title="This project has no invited members" />;
  }

  function onSelectOption(
    { target }: SelectedCardItemOption,
    member: FaroCoreApiTypes.ProjectMemberBase
  ): void {
    if (target.value === "remove") {
      setMemberToRemove(member);
    }
  }

  return (
    <>
      <Stack gap={2} sx={{ maxHeight: "300px", overflow: "auto" }}>
        {members.map((member) => (
          <ProjectMember
            key={member.identity}
            member={member}
            shouldHideRemove={
              !canDeleteMemberFromProject || member.role === "RESOURCE_OWNER"
            }
            isRoleEditable={canChangeRole}
            onSelectOption={onSelectOption}
            projectId={project.id}
            location={location}
          />
        ))}
      </Stack>

      <Dialog
        title="Remove from project"
        confirmText="Remove"
        open={memberToRemove !== null}
        onConfirm={removeMember}
        onCancel={() => setMemberToRemove(null)}
        showSpinner={isRemovingProjectMember || isRemovingMember}
        disabled={isRemovingProjectMember || isRemovingMember}
      >
        <Text
          variant="bodyM"
          sx={{
            marginBottom: SPACE_ELEMENTS_OF_MODAL,
          }}
        >
          When removing a member from the project they may no longer be able to
          access it. You can always invite this member again.
        </Text>

        {memberToRemove && <UserDisplay member={memberToRemove} />}
      </Dialog>
    </>
  );
}
